/* @flow */

import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { CombinedReducers } from '../../../reducers';
import type { Dispatch } from '../../../types/types';
import type { RequestResponseMethodDefinitionType } from '../emitter';

import AccurateTimestamp from '../../../../helpers/dateTime/AccurateTimestamp';
import { isUndefinedOrNull } from '../../../../helpers/jsHelpers/types';
import Localizer from '../../../../helpers/localization';
import { Messenger, MessengerEvents } from '../../../../helpers/messenger';
import ProfilesManager from '../../../../helpers/profiles/profilesManager';
import { CustomNetworkError } from '../../../../libs/netgemLibrary/helpers/CustomNetworkError';
import { PersonalDataKey } from '../../../../libs/netgemLibrary/personalData/constants/keys';
import { HttpStatus } from '../../../../libs/netgemLibrary/v8/constants/NetworkCodesAndMessages';
import { getWishlistTypeFromId } from '../../../../libs/netgemLibrary/v8/helpers/Wishlist';
import { type NETGEM_API_WISHLIST, WishlistItemKind } from '../../../../libs/netgemLibrary/v8/types/Wishlist';
import { clearWishlistETag, resetWishlist, setWishlistError, updateWishlist } from '../../../ui/actions';
import { sendPersonalDataDeleteRequest } from './delete';
import { sendPersonalDataGetRequest } from './get';
import { sendPersonalDataPostRequest } from './post';

const getWishlist: (signal?: AbortSignal) => RequestResponseMethodDefinitionType =
  (signal) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<string | null> => {
    const {
      ui: {
        wishlistETagCache: { wishlist: initialETag },
      },
    } = getState();

    const wishlistKey = ProfilesManager.buildCurrentProfileKey(PersonalDataKey.Wishlist);

    return dispatch(sendPersonalDataGetRequest(wishlistKey, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag: wishlistETag } = data;
        const result = JSON.parse(data.result as string);
        let wishlist: NETGEM_API_WISHLIST = (result.wishlist as NETGEM_API_WISHLIST) || [];

        if (!Array.isArray(wishlist)) {
          wishlist = [];
        }

        // Filter out malformed items
        wishlist = validateWishlist(wishlist);

        dispatch(updateWishlist(wishlist, wishlistETag));
        return wishlistETag;
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (status === HttpStatus.NotFound) {
          // Not found means wishlist does not exist yet
          dispatch(resetWishlist());
          return null;
        } else if (status === HttpStatus.NotModified) {
          return initialETag;
        }

        dispatch(setWishlistError());
        throw error;
      });
  };

const validateWishlist: (wishlist: NETGEM_API_WISHLIST) => NETGEM_API_WISHLIST = (wishlist) => {
  const validatedWishlist = [];

  for (let i = 0; i < wishlist.length; ++i) {
    const { [i]: item } = wishlist;
    const { channelId, date, id, type } = item;

    if ((channelId === undefined || typeof channelId === 'string') && typeof date === 'string' && typeof id === 'string' && (type === WishlistItemKind.Program || type === WishlistItemKind.Series)) {
      validatedWishlist.push(item);
    }
  }

  return validatedWishlist;
};

const findAssetId: (wishlist: NETGEM_API_WISHLIST, assetId: string, channelId: string, acceptNotMatchingChannelId?: boolean) => number = (wishlist, assetId, channelId, acceptNotMatchingChannelId) => {
  for (let i = 0; i < wishlist.length; i++) {
    const {
      [i]: { channelId: chId, id },
    } = wishlist;

    // Channel Id is null for items added in first version of wishlist (backward compatibility)
    if (id === assetId && (chId === channelId || (acceptNotMatchingChannelId && chId && chId !== channelId) || isUndefinedOrNull(chId))) {
      return i;
    }
  }

  return -1;
};

const clearWishlist: (isSecondAttempt?: boolean, eTag?: string) => RequestResponseMethodDefinitionType =
  (isSecondAttempt, eTag) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: {
        wishlistETagCache: { wishlist: initialETag },
      },
    } = getState();
    const wishlist: NETGEM_API_WISHLIST = [];

    const wishlistKey = ProfilesManager.buildCurrentProfileKey(PersonalDataKey.Wishlist);

    return dispatch(sendPersonalDataPostRequest(wishlistKey, { wishlist }, eTag ?? initialETag))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag: wishlistETag } = data;
        dispatch(updateWishlist(wishlist, wishlistETag));
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (!isSecondAttempt && (status === HttpStatus.Conflict || status === HttpStatus.PreconditionFailed || status === HttpStatus.PreconditionRequired)) {
          /*
           * Most probable cause: another device paired with the same account already updated the wishlist
           * So we send a GET request to retrieve the latest ETag, but we only try once to avoid infinite loop between multiple devices
           */
          dispatch(clearWishlistETag());
          return dispatch(getWishlist())
            .then((newETag) => dispatch(clearWishlist(true, newETag)))
            .catch(() => {
              // GET request failed: nothing to clear
            });
        }

        throw error;
      });
  };

const sendWishlistUpdateRequest: (wishlist: NETGEM_API_WISHLIST, initialETag: ?string, signal?: AbortSignal, isSecondAttempt?: boolean) => RequestResponseMethodDefinitionType =
  (wishlist, initialETag, signal, isSecondAttempt) =>
  (dispatch: Dispatch): Promise<void> =>
    dispatch(sendPersonalDataPostRequest(ProfilesManager.buildCurrentProfileKey(PersonalDataKey.Wishlist), { wishlist }, initialETag, signal))
      .then((data: NETGEM_API_V8_REQUEST_RESPONSE) => {
        const { eTag: wishlistETag } = data;
        dispatch(updateWishlist(wishlist, wishlistETag));
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (!isSecondAttempt && (status === HttpStatus.Conflict || status === HttpStatus.PreconditionFailed || status === HttpStatus.PreconditionRequired)) {
          /*
           * Most probable cause: another device paired with the same account already updated the wishlist
           * So we send a GET request to retrieve the latest ETag, but we only try once to avoid infinite loop between multiple devices
           */
          dispatch(clearWishlistETag());

          return dispatch(getWishlist(signal))
            .then((newETag) => dispatch(sendWishlistUpdateRequest(wishlist, newETag, signal, true)))
            .catch(() => {
              // GET request failed: nothing to update
              Messenger.emit(MessengerEvents.NOTIFY_ERROR, Localizer.localize('settings.content.wishlist.update_error'));
              throw error;
            });
        }

        throw error;
      });

const addToWishlist: (assetId: string, channelId: string, signal?: AbortSignal, isSecondAttempt?: boolean) => RequestResponseMethodDefinitionType =
  (assetId, channelId, signal, isSecondAttempt) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: {
        wishlist,
        wishlistETagCache: { wishlist: initialETag },
      },
    } = getState();

    if (!wishlist) {
      // Wishlist not retrieved yet
      return Promise.resolve();
    }

    const assetIdIndex = findAssetId(wishlist, assetId, channelId);

    if (assetIdIndex > -1) {
      // Item already in wishlist (should not happen but could be a sync issue)
      return Promise.resolve();
    }

    // Add item
    const newWishlist: NETGEM_API_WISHLIST = [
      ...wishlist,
      {
        channelId,
        date: AccurateTimestamp.nowAsIsoString(),
        id: assetId,
        type: getWishlistTypeFromId(assetId),
      },
    ];

    return dispatch(sendWishlistUpdateRequest(newWishlist, initialETag, signal, isSecondAttempt));
  };

const removeFromWishlist: (assetId: string, channelId: string, signal?: AbortSignal, isSecondAttempt?: boolean) => RequestResponseMethodDefinitionType =
  (assetId, channelId, signal, isSecondAttempt) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: {
        wishlist,
        wishlistETagCache: { wishlist: initialETag },
      },
    } = getState();

    if (!wishlist) {
      // Wishlist not retrieved yet
      return Promise.resolve();
    }

    let assetIdIndex = findAssetId(wishlist, assetId, channelId);

    if (assetIdIndex === -1) {
      // Item not found: let's search again while ignoring the channel Id in case the item was added in the first version (backward compatibility)
      assetIdIndex = findAssetId(wishlist, assetId, channelId, true);

      if (assetIdIndex === -1) {
        // Item not found in wishlist (not supposed to happen but could be a sync issue)
        return Promise.resolve();
      }
    }

    // Remove item
    const newWishlist: NETGEM_API_WISHLIST = wishlist.filter((item, index) => index !== assetIdIndex);

    return dispatch(sendWishlistUpdateRequest(newWishlist, initialETag, signal, isSecondAttempt));
  };

const deleteWishlist: (profileId?: string, eTag?: string, signal?: AbortSignal, isSecondAttempt?: boolean) => RequestResponseMethodDefinitionType =
  (profileId, eTag, signal, isSecondAttempt) =>
  (dispatch: Dispatch, getState: () => CombinedReducers): Promise<void> => {
    const {
      ui: {
        wishlist,
        wishlistETagCache: { wishlist: initialETag },
      },
    } = getState();

    if (!wishlist) {
      // Wishlist not retrieved yet
      return Promise.resolve();
    }

    const wishlistKey = ProfilesManager.buildProfileKey(PersonalDataKey.Wishlist, profileId);

    return dispatch(sendPersonalDataDeleteRequest(wishlistKey, eTag ?? initialETag, signal))
      .then(() => {
        dispatch(clearWishlistETag());
      })
      .catch((error: CustomNetworkError) => {
        const status = error.getStatus();
        if (status === HttpStatus.NotFound) {
          // Wishlist not found for this user: nothing to delete
          return;
        } else if (!isSecondAttempt && (status === HttpStatus.Conflict || status === HttpStatus.PreconditionFailed || status === HttpStatus.PreconditionRequired)) {
          /*
           * Most probable cause: another device paired with the same account already updated the viewing history
           * So we send a GET request to retrieve the latest ETag, but we only try once to avoid infinite loop between multiple devices
           */
          dispatch(clearWishlistETag());

          return dispatch(getWishlist())
            .then((newETag) => {
              if (newETag) {
                return dispatch(deleteWishlist(profileId, newETag, signal, true));
              }

              // GET request failed with expected error: nothing to delete
            })
            .catch(() => {
              // GET request failed with unexpected error: nothing to delete
            });
        }

        throw error;
      });
  };

export { addToWishlist, clearWishlist, deleteWishlist, getWishlist, removeFromWishlist };
